import { GeneratingBusinessNamesLoadingOverlay } from '@/React/FreeTool/BusinessNameGenerator/Component/GeneratingBusinessNamesLoadingOverlay'
import { FlashApiForm } from '@/React/Shared/Component/Flash/FlashApiForm'
import {
    Turnstile,
    useTurnstile,
} from '@/React/Shared/Component/Forms/Turnstile'
import { BusinessNameGeneratorContainer } from '@/React/FreeTool/BusinessNameGenerator/Component/BusinessNameGeneratorContainer'
import { BusinessNameGeneratorStepStartContent } from '@/React/FreeTool/BusinessNameGenerator/Component/BusinessNameGeneratorStepStartContent'
import { useApiForm } from '@/React/Shared/Component/Forms/ApiForm'
import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'
import { RoutePathFreeToolEnum } from '@/Shared/AutoGenerated/RoutePathEnum'
import { FreeToolLayout } from '@/React/FreeTool/Shared/Layout/FreeToolLayout'
import { Card } from '@/React/Shared/Component/Card'
import { ApiFormControl } from '@/React/Shared/Component/Forms/ApiFormControl'
import { ApiFormButton } from '@/React/Shared/Component/Forms/ApiFormButton'
import { IconNameEnum } from '@/React/Shared/Component/Icon'
import { TextareaInput } from '@/React/Shared/Component/Forms/TextareaInput'
import {
    USER_PROMPT_MAX_LENGTH,
    type BEShowBusinessNameGeneratorStepStartPagePropKeys,
    type BusinessNameGeneratorSessionRawData,
    type BusinessNameGeneratorStartPageContent,
    type FreeToolSharedDataRawData,
} from '@/Shared/AutoGenerated/Main'
import FreeToolSharedData from '@/FreeTool/Shared/Entity/FreeToolSharedData'
import type { EnforceBEPagePropKeys } from '@/Shared/Helper/EnforceBEPagePropKeys'
import useDate from '@/Shared/Hook/useDate'
import BusinessNameGeneratorSession from '@/FreeTool/BusinessNameGenerator/Entity/BusinessNameGeneratorSession'
import { useState } from 'react'

type BusinessNameGeneratorHomePageProps = EnforceBEPagePropKeys<
    BEShowBusinessNameGeneratorStepStartPagePropKeys,
    {
        freeToolSharedDataRawData: FreeToolSharedDataRawData
        businessNameGeneratorSessionRawData: BusinessNameGeneratorSessionRawData | null
        startPageContent: BusinessNameGeneratorStartPageContent
        turnstileEnabled: boolean
        turnstileSiteKey: string
    }
>

export default function BusinessNameGeneratorStepStartPage({
    freeToolSharedDataRawData,
    businessNameGeneratorSessionRawData,
    startPageContent,
    turnstileEnabled,
    turnstileSiteKey,
}: BusinessNameGeneratorHomePageProps) {
    const apiForm = useApiForm()
    const turnstile = useTurnstile(turnstileEnabled)
    const browserTimezone = useDate().getBrowserTimeZone()
    const sharedData = FreeToolSharedData.hydrate(freeToolSharedDataRawData)
    const currentSession = BusinessNameGeneratorSession.hydrateSingleOrNull(
        businessNameGeneratorSessionRawData,
    )

    // ---------
    // User prompt character indicator
    // ---------

    const [userPrompt, setUserPrompt] = useState<string>(
        currentSession?.userPrompt ?? '',
    )

    return (
        <FreeToolLayout
            appName={sharedData.appName}
            flashStatus={sharedData.flashStatus}
            hasInternalScroll={false}
            headerRight={
                <span className="text-xs text-gray-500 sm:text-sm">
                    Business name generator
                </span>
            }
        >
            <GeneratingBusinessNamesLoadingOverlay show={apiForm.loading} />

            <BusinessNameGeneratorContainer
                eyebrow="Free AI Tool — No Signup Required"
                title="Business Name Generator for Coaches, Consultants & Solo Experts"
                description={`Find a name for your expertise. Describe what you do, get tailored business names and taglines, and turn your favorite into a ${sharedData.appName} booking page.`}
            >
                <div className="mx-auto max-w-xl">
                    <Card.Container>
                        <Card.Content>
                            <FlashApiForm
                                apiForm={apiForm}
                                method="POST"
                                url={
                                    RoutePathFreeToolEnum.BusinessNameGenerator_StepStart
                                }
                            >
                                <ApiFormControl
                                    inputName={
                                        RequestBodyKeyEnum.FreeTool_BusinessNameGenerator_Description
                                    }
                                    label="What do you do, and who do you help?"
                                    helperText={`A sentence or two is enough, ${USER_PROMPT_MAX_LENGTH - userPrompt.length} characters left.`}
                                    apiFormResponse={apiForm.response}
                                    className="mb-6"
                                    required
                                >
                                    <TextareaInput
                                        name={
                                            RequestBodyKeyEnum.FreeTool_BusinessNameGenerator_Description
                                        }
                                        defaultValue={
                                            currentSession?.userPrompt ?? ''
                                        }
                                        required
                                        maxLength={USER_PROMPT_MAX_LENGTH}
                                        rows={5}
                                        placeholder="e.g. I’m a career coach helping mid-career professionals move into leadership roles with more confidence."
                                        className="block min-h-40 resize-y text-base leading-relaxed"
                                        onChange={(event) =>
                                            setUserPrompt(
                                                event.target.value ?? '',
                                            )
                                        }
                                    />
                                </ApiFormControl>

                                <ApiFormButton
                                    apiForm={apiForm}
                                    loading={!turnstile.isReady}
                                    loadingText="Loading security check..."
                                    color="primary"
                                    iconName={IconNameEnum.ArrowRight}
                                    iconRight
                                    className="w-full"
                                >
                                    Generate business names
                                </ApiFormButton>

                                <div className="flex items-center justify-center mt-6">
                                    <Turnstile
                                        turnstile={turnstile}
                                        siteKey={turnstileSiteKey}
                                        response={apiForm.response}
                                    />
                                </div>

                                <input
                                    type="hidden"
                                    name={
                                        RequestBodyKeyEnum.FreeTool_BusinessNameGenerator_BrowserTimezone
                                    }
                                    value={browserTimezone}
                                />
                            </FlashApiForm>
                        </Card.Content>
                    </Card.Container>
                </div>
            </BusinessNameGeneratorContainer>

            <BusinessNameGeneratorStepStartContent
                content={startPageContent}
                appName={sharedData.appName}
            />
        </FreeToolLayout>
    )
}
